import { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon, DocumentTextIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { api, ApiError, type FieldWellnessWorkspace } from '../lib/api.js';
import { FieldWellnessEditor } from './FieldWellness.js';

function failureMessage(error: unknown): string {
  const reason = error instanceof ApiError ? error.reason : error instanceof Error ? error.message : '';
  if (reason === 'field_wellness_case_officer_not_configured') return 'An administrator must select the shared case officer before you can start a form.';
  if (reason === 'field_wellness_case_unavailable') return 'This case is not available to your account. Refresh the list or contact your administrator.';
  return 'The case list or saved form could not be opened. Check your connection and try again.';
}

export function FieldWellnessScreen({ onManageTemplates }: { onManageTemplates?: () => void } = {}) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'cases' | 'drafts'>('cases');
  const [offset, setOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState<FieldWellnessWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDraft, setActiveDraft] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const openingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    if (activeDraft) return;
    let current = true;
    setLoading(true); setError(null); setData(null);
    void api.fieldWellnessWorkspace(query, view, offset).then((result) => {
      if (!current) return;
      if (result.protectionProfile !== 'aag_trident_fips203_204_205_v1') throw new Error('unverified_form_service');
      setData(result);
    }).catch((cause) => { if (current) setError(failureMessage(cause)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [activeDraft, query, view, offset, refresh]);

  async function openCase(row: FieldWellnessWorkspace['rows'][number]) {
    if (openingRef.current) return;
    const legacyId = data?.legacyDraft?.caseNumber === row.caseNumber ? data.legacyDraft.draftId : null;
    if (row.draftId || legacyId) { setActiveDraft(row.draftId ?? legacyId); return; }
    openingRef.current = true;
    setOpening(row.caseId); setError(null);
    try {
      const draft = await api.startFieldWellnessDraft(row.caseId);
      if (!draft.draftId || draft.protectionProfile !== 'aag_trident_fips203_204_205_v1') throw new Error('unverified_form_service');
      if (mounted.current) setActiveDraft(draft.draftId);
    } catch (cause) { if (mounted.current) setError(failureMessage(cause)); }
    finally { openingRef.current = false; if (mounted.current) setOpening(null); }
  }

  if (activeDraft) return <FieldWellnessEditor key={activeDraft} draftId={activeDraft}
    onClose={() => { setActiveDraft(null); setRefresh((value) => value + 1); }} />;

  return <section aria-labelledby="wellness-workspace-title" className="wellness-form">
    <p className="text-h6 uppercase text-accent-700">Field wellness</p>
    <h2 id="wellness-workspace-title" className="text-h3 mt-1">Cases and saved forms</h2>
    {onManageTemplates && <button type="button" className="btn btn-secondary mt-3" disabled={Boolean(opening)} onClick={onManageTemplates}>
      <DocumentTextIcon className="h-5 w-5" aria-hidden /> Manage note templates
    </button>}
    <p className="mt-2 text-neutral-700">Find a case to start a form, or resume your unfinished work. Save and close when you need to return another day.</p>
    <form role="search" className="mt-4" onSubmit={(event) => {
      event.preventDefault(); setQuery(search.trim()); setOffset(0); setRefresh((value) => value + 1);
    }}>
      <label className="field" htmlFor="wellness-case-search">Case number</label>
      <div className="flex gap-2">
        <input id="wellness-case-search" className="input min-w-0" type="search" inputMode="search"
          placeholder="14-00001 or part of the number" value={search} maxLength={8} autoComplete="off"
          onChange={(event) => setSearch(event.target.value.replace(/[^0-9-]/gu, ''))} />
        <button type="submit" className="btn btn-primary shrink-0" disabled={Boolean(opening)}>
          <MagnifyingGlassIcon className="h-5 w-5" aria-hidden /> Search
        </button>
      </div>
    </form>
    <div className="mt-4 grid grid-cols-2 gap-2" aria-label="Form list filters">
      <button type="button" className={`btn ${view === 'cases' ? 'btn-primary' : 'btn-secondary'}`}
        aria-pressed={view === 'cases'} disabled={Boolean(opening)} onClick={() => { setView('cases'); setOffset(0); }}>Cases</button>
      <button type="button" className={`btn ${view === 'drafts' ? 'btn-primary' : 'btn-secondary'}`}
        aria-pressed={view === 'drafts'} disabled={Boolean(opening)} onClick={() => { setView('drafts'); setOffset(0); }}>My unfinished forms</button>
    </div>
    {error && <div className="form-error mt-4" role="alert">
      <p>{error}</p>
      <button type="button" className="btn btn-secondary mt-3" onClick={() => setRefresh((value) => value + 1)}>
        <ArrowPathIcon className="h-5 w-5" aria-hidden /> Try again
      </button>
    </div>}
    {loading && <p className="mt-4" role="status">Loading cases and saved forms…</p>}
    {!loading && data && <>
      {data.legacyDraft && offset === 0 && <article className="mt-4 border border-accent-300 bg-accent-100 p-4">
        <h3 className="font-semibold">Earlier saved form{data.legacyDraft.caseNumber ? `: ${data.legacyDraft.caseNumber}` : ''}</h3>
        <p className="mt-1 text-sm">Your previous draft is preserved. Resume it here; no answers need to be re-entered.</p>
        <button type="button" className="wellness-nav-button mt-3" disabled={Boolean(opening)}
          onClick={() => setActiveDraft(data.legacyDraft!.draftId)}>Resume earlier form</button>
      </article>}
      <p className="mt-4 text-sm text-neutral-700" role="status">{data.rows.length
        ? `Showing ${offset + 1} to ${offset + data.rows.length}${data.hasMore ? '. More cases are available.' : '.'}`
        : data.legacyDraft ? 'Your earlier saved form is available above.'
        : view === 'drafts' ? 'No unfinished forms match this search.' : 'No permitted cases match this search.'}</p>
      <ul className="mt-2 divide-y divide-neutral-300">
        {data.rows.map((row) => {
          const resume = Boolean(row.draftId || data.legacyDraft?.caseNumber === row.caseNumber);
          return <li key={row.caseId} className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="min-w-0">
              <h3 className="text-h4 font-semibold">{row.caseNumber}</h3>
              <p className="text-sm text-neutral-700">{resume ? row.review ? 'Ready for review' : 'In progress' : 'Ready to start'}</p>
              {row.updatedAt && <p className="text-xs text-neutral-700">Saved {new Date(row.updatedAt).toLocaleString()}</p>}
              {!row.draftId && row.lastSubmittedAt && <p className="text-xs text-neutral-700">Last submitted {new Date(row.lastSubmittedAt).toLocaleDateString()}. Starting again creates a separate visit.</p>}
            </div>
            <button type="button" className={`wellness-nav-button desktop:!w-auto ${resume ? 'wellness-nav-button--forward' : ''}`}
              disabled={Boolean(opening)} aria-label={`${resume ? 'Resume' : 'Start form for'} ${row.caseNumber}`}
              onClick={() => void openCase(row)}>
              <DocumentTextIcon className="h-5 w-5" aria-hidden />{opening === row.caseId ? 'Opening…' : resume ? 'Resume' : 'Start form'}
            </button>
          </li>;
        })}
      </ul>
      {(offset > 0 || data.hasMore) && <nav className="wellness-navigation" aria-label="Case result pages">
        <button type="button" className="wellness-nav-button" disabled={offset === 0 || Boolean(opening)} onClick={() => setOffset(Math.max(0, offset - 50))}>Previous</button>
        <button type="button" className="wellness-nav-button" disabled={!data.hasMore || Boolean(opening)} onClick={() => setOffset(offset + 50)}>Next</button>
      </nav>}
    </>}
  </section>;
}
